import { useCallback, useMemo } from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { useConfirm, usePrompt, useAlert } from '../../components/dialogs/DialogProvider';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { ApiError } from '../../api/client';
import type { PendingReissueRow } from '../../api/types';
import {
  useApproveCertReissue,
  useCertReissueRequestsList,
  useMyPermissions,
  useRejectCertReissue,
} from '../../api/queries';

/** Grid row = the approver feed row plus the `id` the grid keys on (= requestId). */
type ReissueGridRow = PendingReissueRow & { id: string };

export const APPROVE_PERMISSION = 'provisioning.site-controllers.cert-reissue-approve';

/**
 * SC SSH Admin Access & Recovery — Phase 1, Task I1. Approver action-item
 * surface for the two-visit device-cert recovery (Draft-4 §7 / group G).
 *
 * A senior tech / OperatorAdmin holding cert-reissue-approve sees every
 * `pending_approval` reissue request and weighs the trust signals (days the SC
 * has been offline, the hardware cross-check, whether the device signature over
 * the challenge nonce re-verifies) before Approve / Reject. Reject prompts for a
 * reason (audited) and opens a 24h cooldown server-side. All ids resolve to
 * labels (SC QR Code, operator name) — no raw GUIDs. The whole surface is hidden
 * from non-holders.
 */
export default function CertReissueApprovalsPage() {
  usePageTitle('Recovery approvals');
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );
  const canApprove = has(APPROVE_PERMISSION);

  const list = useCertReissueRequestsList('pending_approval');
  const listFn = useCallback(
    async (): Promise<ReissueGridRow[]> => {
      const rows = await list.listFn();
      return rows.map((r) => ({ ...r, id: r.requestId }));
    },
    [list],
  );

  const approve = useApproveCertReissue();
  const reject = useRejectCertReissue();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const alert = useAlert();

  const columns = useMemo<GridColDef<ReissueGridRow>[]>(() => [
    {
      field: 'siteControllerLabel', headerName: 'Site controller', flex: 1.3,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          {p.row.qrCode
            ? <code style={{ fontSize: 11 }}>{p.row.qrCode}</code>
            : <Typography variant="body2" color="text.secondary">— (no QR Code)</Typography>}
          <Typography variant="caption" color="text.secondary">
            {fmtOrEmDash(p.row.operatorName)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'daysOffline', headerName: 'Offline', width: 120,
      type: 'number', align: 'right', headerAlign: 'right',
      renderCell: (p) => {
        const d = p.row.daysOffline;
        if (d === null || d === undefined) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        return <Chip size="small" label={`${d}d`} color={d >= 7 ? 'error' : d >= 1 ? 'warning' : 'default'} />;
      },
    },
    {
      field: 'deviceSignatureVerified', headerName: 'Device signature', width: 170,
      renderCell: (p) => p.row.deviceSignatureVerified
        ? <Chip size="small" label="verified" color="success" />
        : <Chip size="small" label="NOT verified" color="error" />,
    },
    {
      field: 'hardwareCrossCheck', headerName: 'Hardware', width: 160,
      renderCell: (p) => (
        <Tooltip title={p.row.boardModel ? `board: ${p.row.boardModel}` : 'board model not captured'}>
          <span>{fmtOrEmDash(p.row.hardwareCrossCheck)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'requestedAt', headerName: 'Requested', width: 180,
      valueFormatter: (value: unknown) => value ? fmtDateTime(value) : '—',
    },
  ], []);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text', key: 'siteControllerLabel', label: 'Site controller (QR Code)' },
    { kind: 'text', key: 'operatorName', label: 'Operator' },
  ], []);

  const quickFilters = useMemo<QuickFilter<ReissueGridRow>[]>(() => [
    { label: 'All', filter: () => true },
    { label: 'Signature verified', filter: (r) => r.deviceSignatureVerified },
    { label: 'Signature failed', filter: (r) => !r.deviceSignatureVerified },
    { label: 'Offline 7d+', filter: (r) => (r.daysOffline ?? 0) >= 7 },
  ], []);

  const exportColumns = useMemo<ExportColumn<ReissueGridRow>[]>(() => [
    { key: 'qrCode', label: 'QR Code', format: (v) => fmtOrEmDash(v) },
    { key: 'operatorName', label: 'Operator', format: (v) => fmtOrEmDash(v) },
    { key: 'state', label: 'State' },
    { key: 'daysOffline', label: 'Days offline', format: (v) => v === null || v === undefined ? '—' : String(v) },
    { key: 'deviceSignatureVerified', label: 'Device signature', format: (v) => v ? 'verified' : 'NOT verified' },
    { key: 'hardwareCrossCheck', label: 'Hardware cross-check', format: (v) => fmtOrEmDash(v) },
    { key: 'boardModel', label: 'Board model', format: (v) => fmtOrEmDash(v) },
    { key: 'reasonCode', label: 'Reason code', format: (v) => fmtOrEmDash(v) },
    { key: 'requestedAt', label: 'Requested', format: (v) => v ? fmtDateTime(v) : '—' },
  ], []);

  const runApprove = useCallback(async (row: ReissueGridRow) => {
    const ok = await confirm({
      title: 'Approve cert reissue',
      message: (
        <>
          Approve the device-certificate recovery for{' '}
          <strong>{row.qrCode ?? row.siteControllerLabel}</strong>
          {row.operatorName ? <> ({row.operatorName})</> : null}? The fresh cert is
          minted at the technician's second site visit (delivery), not now.
        </>
      ),
      confirmLabel: 'Approve',
    });
    if (!ok) return;
    approve.mutate(
      { scId: row.siteControllerId, requestId: row.requestId },
      {
        onError: (e) => alert({
          title: 'Approve failed',
          message: (e as unknown as ApiError).message ?? 'The approval did not complete.',
          severity: 'error',
        }),
      },
    );
  }, [approve, confirm, alert]);

  const runReject = useCallback(async (row: ReissueGridRow) => {
    const reason = await prompt({
      title: 'Reject cert reissue',
      message: (
        <>
          Rejecting opens a 24-hour cooldown before this site controller can request
          recovery again. Give a reason (audited).
        </>
      ),
      placeholder: 'e.g. could not confirm technician identity on site',
      multiline: true,
      confirmLabel: 'Reject',
      destructive: true,
    });
    if (reason === null) return; // cancelled
    reject.mutate(
      { scId: row.siteControllerId, requestId: row.requestId, reason: reason.trim() || null },
      {
        onError: (e) => {
          const err = e as unknown as ApiError;
          // 423 = a reject cooldown is already active for this SC.
          const msg = err.status === 423 || err.code === 'reject_cooldown'
            ? 'A recent reject is still in its 24-hour cooldown for this site controller. An approver may override via a fresh request.'
            : (err.message ?? 'The rejection did not complete.');
          alert({ title: 'Reject failed', message: msg, severity: 'error' });
        },
      },
    );
  }, [reject, prompt, alert]);

  const actions = useMemo<RowAction<ReissueGridRow>[]>(() => [
    { label: 'Approve', onClick: (row) => void runApprove(row) },
    { label: 'Reject', destructive: true, onClick: (row) => void runReject(row) },
  ], [runApprove, runReject]);

  if (!canApprove) {
    return (
      <Typography color="text.secondary">
        You don't have permission to view cert-reissue recovery approvals.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h4">Recovery approvals</Typography>
      <Typography variant="body2" color="text.secondary">
        SC SSH device-certificate recovery (Phase 1 §7). Two-person control —
        approve or reject a technician's request to reissue the mTLS cert of a
        site controller whose cert lapsed while offline. Weigh the device-signature
        re-verification, days offline, and hardware cross-check before approving.
      </Typography>

      <RouteDataGrid<ReissueGridRow>
        entityName="Recovery approvals"
        queryKey={list.queryKey}
        listFn={listFn}
        columns={columns}
        searchFields={searchFields}
        quickFilters={quickFilters}
        exportColumns={exportColumns}
        reportTitle="ARMS — Cert-reissue recovery approvals"
        actions={actions}
        emptyMessage="No pending recovery approvals."
      />
    </Stack>
  );
}
